import React, { useState } from 'react';
import { Users, GripHorizontal, ArrowRightLeft, ShieldCheck, Download } from 'lucide-react';
import { Card, Button, Input } from '../ui';
import { cn } from '../../lib/utils';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import toast from 'react-hot-toast';

export function SnakeDraftTeamBalancer() {
  const [inputText, setInputText] = useState('');
  const [numTeams, setNumTeams] = useState<number>(4);
  const [teams, setTeams] = useState<string[][]>([]);
  const [isGenerated, setIsGenerated] = useState(false);

  const { hasActiveNode, nodeName } = useNodeRoster();
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);

  const handleImportFromNode = (selectedMembers: DirectoryMember[]) => {
    if (selectedMembers.length === 0) return;
    const names = selectedMembers.map(m => m.name).filter(Boolean);
    const existing = inputText.trim() ? inputText.trim() + '\n' : '';
    setInputText(existing + names.join('\n'));
    toast.success(`Imported ${names.length} members from ${nodeName || 'organization'}`);
  };

  const generateDraft = () => {
    // 1. Parse and shuffle input
    let players = inputText.split('\n').map(p => p.trim()).filter(p => p.length > 0);
    if (players.length < numTeams) {
      alert(`Need at least ${numTeams} players to form ${numTeams} teams.`);
      return;
    }

    // Shuffle array for pure randomness
    players = players.sort(() => Math.random() - 0.5);

    // 2. Initialize empty teams
    const draft: string[][] = Array.from({ length: numTeams }, () => []);

    // 3. Snake Draft Distribution
    let currentTeamIndex = 0;
    let direction = 1; // 1 for forward, -1 for backward

    players.forEach((player) => {
      draft[currentTeamIndex].push(player);

      // Move to next team
      currentTeamIndex += direction;

      // Handle the "snake" bounce at the edges
      if (currentTeamIndex >= numTeams) {
        direction = -1;
        currentTeamIndex = numTeams - 1;
      } else if (currentTeamIndex < 0) {
        direction = 1;
        currentTeamIndex = 0;
      }
    });

    setTeams(draft);
    setIsGenerated(true);
  };

  const copyToClipboard = () => {
    let text = "Official Team Rosters\n\n";
    teams.forEach((team, idx) => {
      text += `--- Team ${idx + 1} ---\n`;
      team.forEach(player => text += `${player}\n`);
      text += "\n";
    });
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center gap-8 border-b-8 border-red-900">
        <div className="absolute top-0 right-0 p-8 opacity-20 pointer-events-none rotate-12">
          <ArrowRightLeft className="w-64 h-64" />
        </div>
        <div className="flex-1 z-10">
          <div className="flex items-center gap-2 text-orange-200 font-bold tracking-widest uppercase mb-4 text-sm">
            <ShieldCheck className="w-4 h-4" /> Team Balancer
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
            Fair Snake-Draft Generator
          </h2>
          <p className="text-orange-100 max-w-2xl text-lg font-medium">
            Don't let kids feel picked last. Paste a list of players and we'll instantly generate perfectly balanced, randomized teams using a snake-draft algorithm (1-2-3-3-2-1).
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        
        {/* Input Column */}
        <div className="md:col-span-4 space-y-6">
          <Card className="p-6 bg-white dark:bg-slate-900 border-2 shadow-xl">
            <h3 className="font-bold text-xl mb-6 flex items-center">
              <Users className="w-6 h-6 mr-2 text-orange-500" /> Draft Settings
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Number of Teams</label>
                <select 
                  value={numTeams}
                  onChange={(e) => setNumTeams(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 16].map(n => (
                    <option key={n} value={n}>{n} Teams</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Player Roster (1 per line)</label>
                  {hasActiveNode && (
                    <button
                      type="button"
                      onClick={() => setIsRosterModalOpen(true)}
                      className="text-xs font-mono font-bold uppercase tracking-wider text-orange-600 hover:text-orange-700 dark:text-orange-400 flex items-center gap-1 cursor-pointer"
                    >
                      <Users size={14} /> Import from {nodeName || 'Org'}
                    </button>
                  )}
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Paste player names here..."
                  className="w-full h-64 p-4 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none resize-none font-medium bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>

              <Button 
                onClick={generateDraft}
                disabled={inputText.trim().length === 0}
                className="w-full h-14 text-lg font-black bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-600/30 uppercase tracking-wide"
              >
                <GripHorizontal className="w-5 h-5 mr-2" /> Execute Draft
              </Button>
            </div>
          </Card>
        </div>

        {/* Results Column */}
        <div className="md:col-span-8">
          {!isGenerated ? (
            <Card className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 bg-slate-50 dark:bg-slate-900 border-dashed border-4 border-slate-200 dark:border-slate-800">
              <ArrowRightLeft className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
              <h3 className="text-2xl font-black text-slate-400 dark:text-slate-600">Waiting for roster...</h3>
              <p className="text-slate-500 mt-2 max-w-sm">Your perfectly balanced teams will appear here once the draft completes.</p>
            </Card>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-end bg-white dark:bg-slate-900 p-4 rounded-xl border shadow-sm">
                <div>
                  <h3 className="text-2xl font-black text-slate-800 dark:text-white uppercase">Official Rosters</h3>
                  <p className="text-sm text-slate-500">Draft completed successfully.</p>
                </div>
                <Button variant="secondary" onClick={copyToClipboard} className="border-orange-200 text-orange-700 hover:bg-orange-50">
                  <Download className="w-4 h-4 mr-2" /> Copy to Clipboard
                </Button>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((team, idx) => (
                  <Card key={idx} className="p-0 overflow-hidden border-t-4 border-t-orange-500 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="bg-slate-50 dark:bg-slate-900 px-4 py-3 border-b dark:border-slate-800 flex justify-between items-center">
                      <span className="font-black text-lg text-slate-800 dark:text-white">Team {idx + 1}</span>
                      <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded-full">{team.length} Players</span>
                    </div>
                    <div className="p-2">
                      <ul className="space-y-1">
                        {team.map((player, pIdx) => (
                          <li key={pIdx} className="px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-950 rounded border border-slate-100 dark:border-slate-800 flex items-center">
                            <span className="w-6 text-slate-400 text-xs">{pIdx + 1}.</span> {player}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      {hasActiveNode && (
        <NodeRosterPickerModal
          isOpen={isRosterModalOpen}
          onClose={() => setIsRosterModalOpen(false)}
          onImport={handleImportFromNode}
          title={`Import ${nodeName || 'Organization'} Players`}
          description={`Select members from ${nodeName} to add to the draft pool.`}
          confirmLabel="Add to Draft Pool"
        />
      )}
    </div>
  );
}
